<div class="resources-page">
  <div class="page-header page-header--no-search">
    <div class="page-header__title">
      <h2>{{ 'Worker resource profiles' | translate }}</h2>
      <p>{{ 'Runtime-safe limits and assignment caps update automatically; queue parallelism and GDAL cache require a worker restart' | translate }}</p>
    </div>
  </div>

  @if (errorMessage) { <div class="error-message">{{ errorMessage | translate }}</div> }
  @if (loading) {
    <app-loading [loading]="true"></app-loading>
  } @else {
    <div class="orch-table-panel">
    <app-orchestration-table-heading heading="Worker resource profiles" [query]="searchQuery" (queryChange)="onCatalogSearch($event)">
      <div tableActions class="table-actions">
        <app-primary-button title="Create profile" icon="pi pi-plus" (action)="create()"></app-primary-button>
      </div>
    </app-orchestration-table-heading>
    <div class="table-wrapper">
      <table>
        <thead><tr>
          <th>{{ 'Profile' | translate }}</th><th>{{ 'Version' | translate }}</th>
          <th>{{ 'Runtime concurrency' | translate }}</th><th>{{ 'Queue parallelism' | translate }}</th>
          <th>{{ 'Concurrent assignment caps' | translate }}</th>
          <th>{{ 'Application state' | translate }}</th><th>{{ 'Actions' | translate }}</th>
        </tr></thead>
        <tbody>
          @for (profile of profiles; track profile.id) {
            <tr>
              <td>
                <strong>{{ profile.name }}</strong>
                <app-copyable-identifier [value]="profile.key"></app-copyable-identifier>
              </td>
              <td>v{{ profile.version }}<small>{{ profile.lastUpdatedAt | date: 'medium' }}</small></td>
              <td><span>Copernicus {{ profile.copernicusDownloadConcurrency }}</span><span>MinIO R/W {{ profile.minioReadConcurrency }}/{{ profile.minioWriteConcurrency }}</span><span>GDAL {{ profile.gdalConcurrency }} · {{ profile.gdalCacheMaxMB }} MB</span></td>
              <td><span>D: {{ profile.downloadQueues.critical }}/{{ profile.downloadQueues.high }}/{{ profile.downloadQueues.normal }}/{{ profile.downloadQueues.bulk }}</span><span>P: {{ profile.postProcessQueues.critical }}/{{ profile.postProcessQueues.high }}/{{ profile.postProcessQueues.normal }}/{{ profile.postProcessQueues.bulk }}</span></td>
              <td>
                @if (profile.assignmentCapSummary) {
                  <span>{{ profile.assignmentCapSummary }}</span>
                } @else {
                  <span>{{ 'Process definition defaults' | translate }}</span>
                }
              </td>
              <td>
                <p-tag [value]="(profile.isActive ? 'Active' : 'Inactive') | translate" [severity]="profile.isActive ? 'success' : 'secondary'"></p-tag>
                @if (profile.restartRequired) { <p-tag [value]="'Worker restart required' | translate" severity="warn"></p-tag> }
                <small>{{ 'Workers on this version' | translate }}: {{ profile.appliedWorkerCount }}</small>
              </td>
              <td class="actions">
                <button pButton type="button" icon="pi pi-pencil" [text]="true" [title]="'Edit' | translate" (click)="edit(profile)"></button>
                @if (!profile.isActive) { <button pButton type="button" icon="pi pi-check" [text]="true" [title]="'Activate' | translate" (click)="activate(profile)"></button> }
              </td>
            </tr>
          } @empty { <tr><td colspan="7" class="empty-state">{{ emptyListMessage | translate }}</td></tr> }
        </tbody>
      </table>
    </div>
    </div>
  }
</div>

<p-dialog [resizable]="false" [draggable]="false" [(visible)]="dialogVisible" [modal]="true" [style]="{ width: 'min(860px, 96vw)' }" [header]="(editing ? 'Edit profile' : 'Create profile') | translate">
  @if (errorMessage) { <div class="error-message">{{ errorMessage | translate }}</div> }
  <div class="form-grid">
    <label><span>{{ 'Key' | translate }}</span><input type="text" [(ngModel)]="form.key" /></label>
    <label><span>{{ 'Name' | translate }}</span><input type="text" [(ngModel)]="form.name" /></label>
    <label><span>Copernicus</span><input type="number" min="1" max="64" [(ngModel)]="form.copernicusDownloadConcurrency" /></label>
    <label><span>MinIO read</span><input type="number" min="1" max="64" [(ngModel)]="form.minioReadConcurrency" /></label>
    <label><span>MinIO write</span><input type="number" min="1" max="64" [(ngModel)]="form.minioWriteConcurrency" /></label>
    <label><span>GDAL</span><input type="number" min="1" max="64" [(ngModel)]="form.gdalConcurrency" /></label>
    <label><span>GDAL cache (MB)</span><input type="number" min="64" max="8192" [(ngModel)]="form.gdalCacheMaxMB" /></label>
    <label class="checkbox-label"><input type="checkbox" [(ngModel)]="form.isActive" /><span>{{ 'Active' | translate }}</span></label>
    <fieldset><legend>{{ 'Download queues (Critical / High / Normal / Bulk)' | translate }}</legend><div class="queue-inputs"><input type="number" min="1" max="64" [(ngModel)]="form.downloadQueues.critical" /><input type="number" min="1" max="64" [(ngModel)]="form.downloadQueues.high" /><input type="number" min="1" max="64" [(ngModel)]="form.downloadQueues.normal" /><input type="number" min="1" max="64" [(ngModel)]="form.downloadQueues.bulk" /></div></fieldset>
    <fieldset><legend>{{ 'Post-process queues (Critical / High / Normal / Bulk)' | translate }}</legend><div class="queue-inputs"><input type="number" min="1" max="64" [(ngModel)]="form.postProcessQueues.critical" /><input type="number" min="1" max="64" [(ngModel)]="form.postProcessQueues.high" /><input type="number" min="1" max="64" [(ngModel)]="form.postProcessQueues.normal" /><input type="number" min="1" max="64" [(ngModel)]="form.postProcessQueues.bulk" /></div></fieldset>
    <fieldset class="cap-fieldset">
      <legend>{{ 'Concurrent assignment caps' | translate }}</legend>
      <p class="cap-hint">{{ 'Concurrent assignment cap hint' | translate }}</p>
      <div class="cap-rows">
        @for (row of capRows; track row.key) {
          <div class="cap-row">
            <div class="cap-row__identity">
              <strong>{{ row.label }}</strong>
              <app-copyable-identifier [value]="row.key"></app-copyable-identifier>
            </div>
            <msf-input
              [model]="row.capInput"
              (modelChange)="onCapInput(row, $event)"
              [placeholder]="'Inherit process default' | translate"
            ></msf-input>
          </div>
        }
      </div>
    </fieldset>
  </div>
  <ng-template #footer><button pButton type="button" severity="secondary" [text]="true" [label]="'Cancel' | translate" (click)="dialogVisible = false"></button><button pButton type="button" [loading]="saving" [disabled]="!form.key || !form.name" [label]="'Save' | translate" (click)="save()"></button></ng-template>
</p-dialog>
